import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { gamingAPI } from '../services/api';
// import SeamlessConnection from './SeamlessConnection';
// CrossPlatformGaming component removed - using Parsec instead

const StreamingInterface = ({ session, onClose }) => {
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [streamingUrl, setStreamingUrl] = useState('');

  const establishConnection = useCallback(async () => {
    try {
      setConnectionStatus('connecting');
      
      // Call backend to create SSH tunnel
      const response = await gamingAPI.createTunnel(session.id);
      
      if (response.tunnel_active) {
        setConnectionStatus('connected');
        toast.success('Connection established! Streaming ready.');
      } else {
        throw new Error('Failed to establish tunnel');
      }
    } catch (error) {
      console.error('Connection error:', error);
      setConnectionStatus('error');
      toast.error('Failed to establish connection. Please try manual setup.');
    }
  }, [session.id]);

  useEffect(() => {
    if (session?.stream_url) {
      setStreamingUrl(`http://localhost:47990`);
      establishConnection();
    }
  }, [session, establishConnection]);

  const openMoonlight = () => {
    // Fallback to manual instructions
    const instructions = `
Moonlight Connection Details:
Host: localhost
Port: 47990
Username: sunshine
Password: (session password)
PIN: 1234

If Moonlight doesn't open automatically, please:
1. Open Moonlight client
2. Enter the details above
3. Click Connect
    `;
    
    alert(instructions);
  };

  const copyConnectionDetails = () => {
    const details = `Host: localhost\nPort: 47990\nUsername: sunshine\nPassword: (from session)\nPIN: 1234`;
    navigator.clipboard.writeText(details);
    toast.success('Connection details copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-white">🎮 Cloud Gaming Stream</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {/* Connection Status */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-2">Connection Status</h3>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' :
                connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`}></div>
              <span className="text-white">
                {connectionStatus === 'connected' ? 'Connected & Ready' :
                 connectionStatus === 'connecting' ? 'Establishing Connection...' :
                 'Connection Failed'}
              </span>
            </div>
          </div>

          {/* Session Info */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-2">Session Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Session:</span>
                <span className="text-white ml-2">{session?.session_name}</span>
              </div>
              <div>
                <span className="text-gray-400">GPU:</span>
                <span className="text-white ml-2">{session?.gpu_type}</span>
              </div>
              <div>
                <span className="text-gray-400">Price/Hour:</span>
                <span className="text-white ml-2">{session?.gpu_price_per_hour?.toFixed(2)} MKD</span>
              </div>
              <div>
                <span className="text-gray-400">Status:</span>
                <span className="text-white ml-2">{session?.status}</span>
              </div>
            </div>
          </div>

          {/* Connection Options */}
          {connectionStatus === 'connected' && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Streaming Options</h3>
              
              {/* Option 1: Web-based Streaming */}
              <div className="mb-4">
                <h4 className="text-md font-medium text-white mb-2">🌐 Web-based Streaming</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Stream directly in your browser (experimental)
                </p>
                <button
                  onClick={() => window.open(streamingUrl, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded mr-2"
                >
                  Open Web Stream
                </button>
              </div>

              {/* Option 2: Moonlight Client */}
              <div className="mb-4">
                <h4 className="text-md font-medium text-white mb-2">🌙 Moonlight Client</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Best performance with dedicated client
                </p>
                <div className="space-x-2">
                  <button
                    onClick={openMoonlight}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
                  >
                    Open Moonlight
                  </button>
                  <button
                    onClick={copyConnectionDetails}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                  >
                    Copy Details
                  </button>
                </div>
              </div>

              {/* Connection Details */}
              <div className="bg-gray-700 rounded p-3">
                <h5 className="text-sm font-medium text-white mb-2">Connection Details:</h5>
                <div className="text-xs text-gray-300 space-y-1">
                  <div>Host: localhost</div>
                  <div>Port: 47990</div>
                  <div>Username: sunshine</div>
                  <div>Password: (session password)</div>
                  <div>PIN: 1234</div>
                </div>
              </div>
            </div>
          )}

          {/* Manual Setup Instructions */}
          {connectionStatus === 'error' && (
            <div className="bg-red-900 bg-opacity-30 rounded-lg p-4 border border-red-500">
              <h3 className="text-lg font-semibold text-red-400 mb-2">Manual Setup Required</h3>
              <p className="text-red-300 text-sm mb-3">
                Automatic connection failed. Please follow these steps:
              </p>
              <div className="text-sm text-red-200 space-y-2">
                <div>1. Open Terminal/Command Prompt</div>
                <div>2. Run: <code className="bg-gray-800 px-2 py-1 rounded">ssh -L 47990:localhost:47990 -p 50399 root@HOST</code></div>
                <div>3. Open Moonlight and connect to localhost:47990</div>
                <div>4. Use credentials: sunshine / (session password) / 1234</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamingInterface;
